import { useState } from "react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { Sprout, ShieldCheck, HeartHandshake, ArrowRight, CheckCircle2 } from "lucide-react";

const CATEGORIES = [
  "Produce", "Pantry", "Dairy", "Meat", "Poultry", "Seafood",
  "Bakery", "Honey & Preserves", "Beverages", "Flowers", "Prepared Foods",
];

export default function PartnersApply() {
  const [form, setForm] = useState({
    farm_name: "", owner_name: "", email: "", phone: "",
    location: "", website_url: "",
    categories: [], farm_size_acres: "", annual_volume_estimate: "",
    story: "", referral_source: "", hp_token: "",
    business_address: "", service_regions: "", practices: [], certifications: "", processing_partners: "",
    food_safety_docs: "", shipping_capabilities: "", social_media: "", product_photo_urls: "", insurance_licensing: "", additional_info: "",
  });
  const PRACTICES = ["Grass-fed","Grass-finished","Pasture-raised","Grain-finished","No antibiotics","No added hormones","Regenerative","Dry-aged","Wild-caught","Heritage breed","Certified organic"];
  const togglePractice = (c) => setForm(f => ({ ...f, practices: f.practices.includes(c) ? f.practices.filter(x => x !== c) : [...f.practices, c] }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [applicationId, setApplicationId] = useState(null);
  const [isDuplicate, setIsDuplicate] = useState(false);

  const toggleCategory = (c) => setForm(f => ({
    ...f, categories: f.categories.includes(c)
      ? f.categories.filter(x => x !== c)
      : [...f.categories, c]
  }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const payload = { ...form, farm_size_acres: form.farm_size_acres ? Number(form.farm_size_acres) : null,
        product_photo_urls: form.product_photo_urls.split(",").map(x => x.trim()).filter(Boolean) };
      const { data } = await api.post("/partners/apply", payload);
      setApplicationId(data.application_id);
      setIsDuplicate(!!data.duplicate);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not send your application — try again or email hello@thefarmtoyou.com.");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-16" data-testid="partners-apply-page">
      <Seo title="Apply to sell · The Farm To You" description="Apply as a ranch, livestock farm, fisherman, or seafood partner. Members purchase directly through The Farm To You — you focus on raising and providing premium proteins."
        canonical="/partners/apply"/>

      <div className="grid md:grid-cols-2 gap-12 items-start">
        <div>
          <p className="overline">Partner with us</p>
          <h1 className="font-display text-5xl md:text-6xl mt-3" data-testid="apply-title">Sell through<br/>The Farm To You.</h1>
          <p className="mt-5 text-lg text-[var(--ff-text-secondary)] leading-relaxed">
            The Farm To You is a members-only platform built for ranchers, livestock farmers, fishermen, and seafood partners. We handle the storefront, the customer base, the cold-chain logistics, and the payments — you focus on raising and providing the best proteins in your county.
          </p>

          <div className="mt-10 space-y-5" data-testid="apply-pillars">
            {[
              { icon: HeartHandshake, title: "Direct member sales", body: "Every buyer is a paying member who chose us specifically because they value where their proteins come from." },
              { icon: ShieldCheck, title: "Drop model, not endless inventory", body: "Sell exactly what you have when you have it. We run scheduled drops your members actively wait for." },
              { icon: Sprout, title: "Real partners, real loyalty", body: "We work with ranches, livestock farms, fisheries, and specialty meat producers — no shelf-of-everyone approach." },
            ].map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={i} className="flex gap-4" data-testid={`apply-pillar-${i}`}>
                  <Icon size={22} className="text-[var(--ff-primary)] mt-1 shrink-0"/>
                  <div>
                    <p className="font-display text-lg">{p.title}</p>
                    <p className="text-sm text-[var(--ff-text-secondary)] mt-1 leading-relaxed">{p.body}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-10 card-flat p-5 bg-[var(--ff-primary)]/5 border-[var(--ff-primary)]/20" data-testid="apply-timeline">
            <p className="overline">What happens next</p>
            <ol className="mt-3 text-sm text-[var(--ff-text-secondary)] space-y-2 list-decimal pl-5">
              <li>You submit this application.</li>
              <li>Our partner team reviews within <strong>5 business days</strong>.</li>
              <li>If we're a fit, you receive a login + onboarding checklist.</li>
              <li>You finish your farm profile and we approve it for go-live.</li>
              <li>You ship your first drop. We pay you within 48 hours of fulfillment.</li>
            </ol>
          </div>
        </div>

        <div>
          {applicationId ? (
            <div className="card-flat p-8 text-center" data-testid="apply-success">
              <CheckCircle2 size={48} className="mx-auto text-[var(--ff-primary)]"/>
              <h2 className="font-display text-3xl mt-4">
                {isDuplicate ? "We already have your application" : "Got it — application received"}
              </h2>
              <p className="mt-3 text-[var(--ff-text-secondary)]">
                {isDuplicate
                  ? "We're already reviewing what you sent. Our team will be in touch soon."
                  : "Our partner team will review and reach out within 5 business days."}
              </p>
              <p className="mt-4 text-xs text-[var(--ff-text-muted)]">
                Reference: <span className="font-mono">{applicationId}</span>
              </p>
              <a href="/" className="btn-primary inline-flex mt-6" data-testid="apply-return-home">Back to home</a>
            </div>
          ) : (
            <form onSubmit={submit} className="card-flat p-6 space-y-4 relative" data-testid="apply-form">
              <h2 className="font-display text-2xl">Tell us about your farm</h2>
              <p className="text-sm text-[var(--ff-text-muted)] -mt-2">Fields marked * are required. Nothing here is published until you and we both agree it should be.</p>

              <div className="grid md:grid-cols-2 gap-3">
                <label className="block">
                  <span className="overline">Farm name *</span>
                  <input required maxLength={120} className="input-base mt-2" value={form.farm_name} onChange={e => setForm({...form, farm_name: e.target.value})} data-testid="apply-farm-name"/>
                </label>
                <label className="block">
                  <span className="overline">Owner name *</span>
                  <input required maxLength={120} className="input-base mt-2" value={form.owner_name} onChange={e => setForm({...form, owner_name: e.target.value})} data-testid="apply-owner-name"/>
                </label>
                <label className="block">
                  <span className="overline">Email *</span>
                  <input required type="email" className="input-base mt-2" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="apply-email"/>
                </label>
                <label className="block">
                  <span className="overline">Phone</span>
                  <input className="input-base mt-2" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="apply-phone"/>
                </label>
                <label className="block md:col-span-2">
                  <span className="overline">Location * (City, ST)</span>
                  <input required maxLength={160} className="input-base mt-2" value={form.location} onChange={e => setForm({...form, location: e.target.value})} data-testid="apply-location"/>
                </label>
                <label className="block md:col-span-2">
                  <span className="overline">Website (optional)</span>
                  <input className="input-base mt-2" placeholder="https://" value={form.website_url} onChange={e => setForm({...form, website_url: e.target.value})} data-testid="apply-website"/>
                </label>
                <label className="block">
                  <span className="overline">Farm size (acres)</span>
                  <input type="number" min="0" className="input-base mt-2" value={form.farm_size_acres} onChange={e => setForm({...form, farm_size_acres: e.target.value})} data-testid="apply-acres"/>
                </label>
                <label className="block">
                  <span className="overline">Annual revenue estimate</span>
                  <input placeholder="e.g. $200k" className="input-base mt-2" value={form.annual_volume_estimate} onChange={e => setForm({...form, annual_volume_estimate: e.target.value})} data-testid="apply-volume"/>
                </label>
              </div>

              <div>
                <span className="overline">What do you sell? *</span>
                <div className="mt-3 flex flex-wrap gap-2" data-testid="apply-categories">
                  {CATEGORIES.map(c => {
                    const active = form.categories.includes(c);
                    return (
                      <button type="button" key={c} onClick={() => toggleCategory(c)} data-testid={`apply-cat-${c.toLowerCase().replace(/\s|&/g, "-")}`}
                        className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${active ? "bg-[var(--ff-primary)] text-white border-[var(--ff-primary)]" : "bg-white border-[var(--ff-border)] text-[var(--ff-text-secondary)] hover:border-[var(--ff-primary)]"}`}>
                        {c}
                      </button>
                    );
                  })}
                </div>
              </div>

              <label className="block">
                <span className="overline">Your story *</span>
                <textarea required minLength={50} maxLength={4000} rows={6} className="input-base mt-2"
                  placeholder="Tell us about your farm — what makes it special, who works it, how long it's been in the family, your growing practices, anything that helps us understand the soul of your operation."
                  value={form.story} onChange={e => setForm({...form, story: e.target.value})} data-testid="apply-story"/>
                <p className="text-[11px] text-[var(--ff-text-muted)] mt-1">Minimum 50 characters · {form.story.length}/4000</p>
              </label>

              <div className="pt-4 border-t border-[var(--ff-border)]">
                <p className="overline">Operations &amp; compliance</p>
                <p className="text-xs text-[var(--ff-text-muted)] mt-1">Ranchers ship their own orders, so we review fulfillment and food-safety readiness before approval.</p>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <label className="block"><span className="overline">Business address</span>
                  <input className="input-base mt-2" value={form.business_address} onChange={e => setForm({...form, business_address: e.target.value})} data-testid="apply-business-address"/></label>
                <label className="block"><span className="overline">Service &amp; shipping regions</span>
                  <input className="input-base mt-2" placeholder="e.g. Lower 48, Midwest only" value={form.service_regions} onChange={e => setForm({...form, service_regions: e.target.value})} data-testid="apply-service-regions"/></label>
                <label className="block"><span className="overline">Processing partners</span>
                  <input className="input-base mt-2" placeholder="USDA/state-inspected facility name(s)" value={form.processing_partners} onChange={e => setForm({...form, processing_partners: e.target.value})} data-testid="apply-processing"/></label>
                <label className="block"><span className="overline">Certifications</span>
                  <input className="input-base mt-2" placeholder="AGA, AWA, Organic, etc." value={form.certifications} onChange={e => setForm({...form, certifications: e.target.value})} data-testid="apply-certifications"/></label>
                <label className="block"><span className="overline">Food-safety documentation</span>
                  <input className="input-base mt-2" placeholder="HACCP plan, inspection numbers, links" value={form.food_safety_docs} onChange={e => setForm({...form, food_safety_docs: e.target.value})} data-testid="apply-food-safety"/></label>
                <label className="block"><span className="overline">Shipping capabilities</span>
                  <input className="input-base mt-2" placeholder="Carriers, cold-chain packaging, ship days" value={form.shipping_capabilities} onChange={e => setForm({...form, shipping_capabilities: e.target.value})} data-testid="apply-shipping"/></label>
                <label className="block"><span className="overline">Insurance / licensing</span>
                  <input className="input-base mt-2" placeholder="Liability insurance, licenses held" value={form.insurance_licensing} onChange={e => setForm({...form, insurance_licensing: e.target.value})} data-testid="apply-insurance"/></label>
                <label className="block"><span className="overline">Social media</span>
                  <input className="input-base mt-2" placeholder="@handles or links" value={form.social_media} onChange={e => setForm({...form, social_media: e.target.value})} data-testid="apply-social"/></label>
                <label className="block md:col-span-2"><span className="overline">Product photo links</span>
                  <input className="input-base mt-2" placeholder="Paste image URLs, comma-separated" value={form.product_photo_urls} onChange={e => setForm({...form, product_photo_urls: e.target.value})} data-testid="apply-photos"/></label>
              </div>
              <div>
                <span className="overline">Ranching practices</span>
                <div className="mt-3 flex flex-wrap gap-2" data-testid="apply-practices">
                  {PRACTICES.map(c => <button type="button" key={c} onClick={() => togglePractice(c)} className={`px-3 py-1 rounded-full text-xs border ${form.practices.includes(c) ? "bg-[var(--ff-primary)] text-white border-[var(--ff-primary)]" : "border-[var(--ff-border)]"}`}>{c}</button>)}
                </div>
              </div>
              <label className="block"><span className="overline">Anything else we should know?</span>
                <textarea rows={3} className="input-base mt-2" value={form.additional_info} onChange={e => setForm({...form, additional_info: e.target.value})} data-testid="apply-additional"/></label>

              <label className="block">
                <span className="overline">How did you hear about us?</span>
                <input className="input-base mt-2" value={form.referral_source} onChange={e => setForm({...form, referral_source: e.target.value})} data-testid="apply-referral"/>
              </label>

              {/* Honeypot — opaque name so browser/password-manager autofill never targets it */}
              <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" name="hp_token"
                className="absolute opacity-0 pointer-events-none -z-10 h-0 w-0"
                value={form.hp_token} onChange={e => setForm({...form, hp_token: e.target.value})}
                data-testid="apply-honeypot"/>

              {error && <div className="bg-rose-50 border border-rose-200 p-3 rounded text-sm text-rose-800" data-testid="apply-error">{error}</div>}

              <button type="submit" disabled={busy || form.categories.length === 0} className="btn-primary w-full" data-testid="apply-submit">
                {busy ? "Sending…" : "Submit application"} <ArrowRight size={16}/>
              </button>
              <p className="text-[11px] text-[var(--ff-text-muted)] text-center">
                By submitting you agree to our <a href="/terms" className="underline">Terms</a> and <a href="/privacy" className="underline">Privacy Policy</a>.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
